import { defineComponent as _defineComponent } from 'vue'
import axios from 'axios'
import {
  computed,
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch
} from 'vue'
import { useRoute } from 'vue-router/composables'
import VueCookies from 'vue-cookies'
import { useAuth } from '@/composables/useAuth'
import { useSite } from '@/composables/useSite'
import { useAppDeviceTokenBridge } from '@/composables/useAppDeviceTokenBridge'
import { AUTH_STATE_CHANGED, emitter } from '@/plugins/emitter'
import AppHeader from '@/layouts/AppHeader.vue'
import AppHeaderSpMenu from '@/layouts/AppHeaderSpMenu.vue'
import AppFooter from '@/layouts/AppFooter.vue'
import AppGlobalDialogs from '@/layouts/AppGlobalDialogs.vue'
import AppleRelayEmailNotice from '@/layouts/AppleRelayEmailNotice.vue'
import YagifriendsPageLoader from '@/layouts/YagifriendsPageLoader.vue'
import PrivateRelayWarningDialog from '@/components/auth/PrivateRelayWarningDialog.vue'
import { applyMembership } from '@/composables/yagiprime/usePrimeMembership'
import {
  clearRouteCookie,
  type RouteCookieStore
} from '@/utils/yagiprime/routeCookie'

/** renew2 ヘッダーを出さないルート名 */
type HostVm = {
  $varuna: { endPoints: Record<string, string> }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $globalVar: Record<string, any>
  $yagi: { isFromMynabi: number }
  $oemContext?: { isOemSite?: () => boolean }
  $firebase: {
    user: {
      uid: string
      email?: string | null
      displayName?: string | null
    } | null
  }
  fullscreenLoading?: boolean
}

declare global {
  interface Window {
    MessageInvoker?: { postMessage: (payload: string) => void }
  }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  props: {
  isLight: {
    type: Boolean,
    default: false
  }
},
  setup(__props) {

/**
 * ルートシェル。ヘッダー / フッター / 常設ダイアログは `@/layouts` へ分解済み。
 * Auth 通知は mitt `auth_state_changed`（旧 $eventHub firebase-initialized 購読を置換）。
 */
const HEADERLESS_ROUTE_NAMES = [
  'yagiprimeSAdlpRirekisho',
  'okinaviPage',
  'shogaiPage'
]
const YAGIFRIENDS_PATH_PREFIX = '/yagifriends'
const REQUEST_TIMEOUT_MS = 60000



const vm = getCurrentInstance()!.proxy as unknown as HostVm
const route = useRoute()
const { user, isAuthenticated, getClient, whenAuthReady } = useAuth()
const { appFlag } = useSite()

const csCount = ref(0)
const randomNumber = ref(0)
const eventvalue = ref('')
const fullscreenLoading = ref(false)

function currentFirebaseUser() {
  return getClient()?.user ?? user.value ?? vm.$firebase?.user ?? null
}

function updateDeviceToken(uuid: string, detoken: string) {
  const subformData = new FormData()
  subformData.append('uuid', uuid)
  subformData.append('device_token', detoken)
  axios
    .post(vm.$varuna.endPoints.updateDeviceToken, subformData, {
      timeout: REQUEST_TIMEOUT_MS
    })
    .catch((error) => {
      console.error(error)
    })
}

const { syncCurrentUserDeviceToken } = useAppDeviceTokenBridge({
  currentUser: currentFirebaseUser,
  postDeviceToken: updateDeviceToken
})

watch(
  () => route.query.evnt,
  (evnt) => {
    if (evnt == null || evnt === '') return
    const value = Array.isArray(evnt) ? evnt[0] : evnt
    if (!value) return

    eventvalue.value = String(value)
    ;(VueCookies as any).config(60 * 60 * 72, '')
    ;(VueCookies as any).set('_YAGISHAPP_FROM_NOTIFICATION', eventvalue.value)

    try {
      const messageInvoker = window.MessageInvoker
      if (messageInvoker && typeof messageInvoker.postMessage === 'function') {
        messageInvoker.postMessage(eventvalue.value)
      }
    } catch (error: any) {
      console.warn(`Error occurred while calling postMessage: ${error.message}`)
    }

    if (eventvalue.value === 'idphoto202605') {
      window.location.href = '/static/idphoto-mobile/'
    } else if (eventvalue.value === 'yagidia20260612') {
      window.location.href =
        'https://portal.yagish.jp/know-how/resume-reason/?frm=yagidia20260612'
    }
  },
  { immediate: true }
)

function invokeNative() {
  // no-op（デバッグ用ボタン用）
}

function goBack() {
  window.history.back()
}

function checkMynabi() {
  if (route.query.mynavi === '19xs#rrdfS234') {
    vm.$yagi.isFromMynabi = 1
  }
}

async function authStateChanged() {
  const firebaseUser = currentFirebaseUser()
  if (!firebaseUser) return

  const uid = firebaseUser.uid

  axios
    .get(vm.$varuna.endPoints.isSubscribed + uid + '/', {
      timeout: REQUEST_TIMEOUT_MS
    })
    .then((response) => {
      vm.$globalVar.isYagiPrime = response.data.is_subscribed
      vm.$globalVar.subscription_type = response.data.subscription_type
      vm.$globalVar.isYagiPrimeCancel = response.data.is_cancel
      // 紹介キャンペーンの対象判定（既存 Prime 会員は紹介者としても対象外）にも
      // 同じ値を使う。ここで流し込むことで各画面のシェア導線が再取得しない
      applyMembership(uid, response.data)
      if (!response.data.is_subscribed) return
      // 加入済みなら導線は記録済み。残すと解約後の再加入に古い導線が混ざる
      clearRouteCookie(VueCookies as unknown as RouteCookieStore)

      const formData = new FormData()
      formData.append('uuid', uid)

      axios
        .post(vm.$varuna.endPoints.subscriptionsMypage, formData, {
          timeout: REQUEST_TIMEOUT_MS
        })
        .then((res) => {
          const _joblist = res.data.recommended_jobs
          for (const _key in _joblist) {
            const _job = _joblist[_key]
            if (!_job.read_date) {
              csCount.value++
            }
          }
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          fullscreenLoading.value = false
          vm.fullscreenLoading = false
        })
    })
    .catch((error) => {
      console.error(error)
    })

  syncCurrentUserDeviceToken()
}

const isAppWebView = computed(() => appFlag.value === 'b')

const isAptitudeRoute = computed(() => route.path.startsWith('/aptitude'))

const isTopPage = computed(() => route.name === 'top')

const isOnlyPreview = computed(
  () => route.name === 'previewOnly' || route.query.noheader === '1'
)

const isYagifriendsRoute = computed(() =>
  route.path.startsWith(YAGIFRIENDS_PATH_PREFIX)
)

// AIダッシュボード（端末高さ固定レイアウト）はフッターを表示しない
const AI_DASHBOARD_PATHS = [
  '/ai/interview_practice',
  '/ai/aptitude_practice',
  '/ai/self_analysis',
  '/ai/career'
]
const isAiDashboardRoute = computed(() =>
  AI_DASHBOARD_PATHS.includes(route.path)
)

const showRenew2Header = computed(
  () =>
    !HEADERLESS_ROUTE_NAMES.includes(String(route.name)) &&
    !isYagifriendsRoute.value &&
    !isAppWebView.value &&
    !isOnlyPreview.value
)

const showRenew2Footer = computed(
  () =>
    !isAppWebView.value &&
    !isOnlyPreview.value &&
    !isYagifriendsRoute.value &&
    !isAiDashboardRoute.value
)

const isOemDisplayAllowed = computed(() => {
  if (vm.$oemContext && typeof vm.$oemContext.isOemSite === 'function') {
    return !vm.$oemContext.isOemSite()
  }
  return true
})

onBeforeMount(() => {
  const formData = new FormData()
  formData.append('app_flag', appFlag.value)
  axios
    .post(vm.$varuna.endPoints.siteSetting, formData, {
      timeout: REQUEST_TIMEOUT_MS
    })
    .then((response) => {
      if (response.data.in_app_payments === 0) {
        vm.$globalVar.in_app_payments = false
      }
    })
    .catch((error) => {
      console.error(error)
    })

  checkMynabi()
  randomNumber.value = Math.floor(Math.random() * 2)

  emitter.on(AUTH_STATE_CHANGED, authStateChanged)
  whenAuthReady()
    .then(() => authStateChanged())
    .catch(() => {})
})

onBeforeUnmount(() => {
  emitter.off(AUTH_STATE_CHANGED, authStateChanged)
})

return { __sfc: true,HEADERLESS_ROUTE_NAMES, YAGIFRIENDS_PATH_PREFIX, REQUEST_TIMEOUT_MS, vm, route, user, isAuthenticated, getClient, whenAuthReady, appFlag, csCount, randomNumber, eventvalue, fullscreenLoading, currentFirebaseUser, updateDeviceToken, syncCurrentUserDeviceToken, invokeNative, goBack, checkMynabi, authStateChanged, isAppWebView, isAptitudeRoute, isTopPage, isOnlyPreview, isYagifriendsRoute, AI_DASHBOARD_PATHS, isAiDashboardRoute, showRenew2Header, showRenew2Footer, isOemDisplayAllowed, AppHeader, AppHeaderSpMenu, AppFooter, AppGlobalDialogs, AppleRelayEmailNotice, YagifriendsPageLoader, PrivateRelayWarningDialog }
}

})